import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  Icon,
  LinearProgress,
  Divider,
  Grid,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import colors from "assets/theme/base/colors";

const initialState = {
  currentPassword: { value: "", error: "" },
  newPassword: { value: "", error: "" },
  confirmPassword: { value: "", error: "" },
};

function ChangePasswordDialog({ open, handleClose }) {
  const [passwordForm, setPasswordForm] = useState(initialState);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    if (!open) {
      setPasswordForm(initialState);
      setLoading(false);
      setShow({ current: false, new: false, confirm: false });
    }
  }, [open]);

  const getPasswordStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    if (score <= 2) return "Weak";
    if (score <= 4) return "Medium";
    return "Strong";
  };

  const getStrengthValue = (strength) => {
    if (strength === "Strong") return 100;
    if (strength === "Medium") return 60;
    return 30;
  };

  const getStrengthColor = (strength) => {
    if (strength === "Strong") return "success";
    if (strength === "Medium") return "warning";
    return "error";
  };

  const toggleVisibility = (key) =>
    setShow((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleChange = (name, value) => {
    setPasswordForm((prev) => ({
      ...prev,
      [name]: { value, error: "" },
    }));
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "currentPassword":
        return !value ? "Please enter current password" : "";
      case "newPassword":
        if (!value) return "Please enter a new password";
        if (value.length < 8) return "Password must contain at least 8 characters";
        if (passwordForm.confirmPassword.value && value !== passwordForm.confirmPassword.value)
          return "Passwords must match";
        return "";
      case "confirmPassword":
        if (!value) return "Please confirm password";
        if (value !== passwordForm.newPassword.value) return "Passwords must match";
        return "";
      default:
        return "";
    }
  };

  const handleOnBlur = (name, value) => {
    const error = getValidationError(name, value);
    setPasswordForm((prev) => ({ ...prev, [name]: { ...prev[name], error } }));
  };

  const validateForm = () => {
    let temp = { ...passwordForm };
    Object.keys(temp).forEach((key) => {
      temp[key] = { ...temp[key], error: getValidationError(key, temp[key].value) };
    });
    setPasswordForm(temp);
    return Object.values(temp).every((f) => f.error === "");
  };

  const handleSubmit = () => {
    if (!validateForm()) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      handleClose();
    }, 1000);
  };

  const strength = passwordForm.newPassword.value
    ? getPasswordStrength(passwordForm.newPassword.value)
    : null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <MDBox textAlign="center">
          <Icon fontSize="large" sx={{ color: colors.deepBlue.main }}>
            lock
          </Icon>
          <MDTypography variant="h5" mt={1}>
            Change Password
          </MDTypography>
          <MDTypography variant="caption" color="text">
            Choose a strong password to keep your account secure
          </MDTypography>
        </MDBox>
      </DialogTitle>

      {/* <Divider /> */}

      <DialogContent>
        <Grid container spacing={2}>
          {/* Current Password */}
          <Grid item xs={12}>
            <RequiredLabel required>Current Password</RequiredLabel>
            <MDInput
              variant="outlined"
              fullWidth
              name="currentPassword"
              type={show.current ? "text" : "password"}
              value={passwordForm.currentPassword.value}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
              error={!!passwordForm.currentPassword.error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggleVisibility("current")} edge="end">
                      <Icon>{show.current ? "visibility_off" : "visibility"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {passwordForm.currentPassword.error && (
              <MDTypography variant="caption" color="error">
                {passwordForm.currentPassword.error}
              </MDTypography>
            )}
          </Grid>

          {/* New Password */}
          <Grid item xs={12}>
            <RequiredLabel required>New Password</RequiredLabel>
            <MDInput
              variant="outlined"
              fullWidth
              name="newPassword"
              type={show.new ? "text" : "password"}
              value={passwordForm.newPassword.value}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
              error={!!passwordForm.newPassword.error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggleVisibility("new")} edge="end">
                      <Icon>{show.new ? "visibility_off" : "visibility"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {passwordForm.newPassword.error && (
              <MDTypography variant="caption" color="error">
                {passwordForm.newPassword.error}
              </MDTypography>
            )}

            {/* Strength bar */}
            {passwordForm.newPassword.value && (
              <MDBox mt={1}>
                <LinearProgress
                  variant="determinate"
                  value={getStrengthValue(strength)}
                  color={getStrengthColor(strength)}
                  sx={{ height: 6, borderRadius: 5 }}
                />
                <MDTypography variant="caption" color={getStrengthColor(strength)}>
                  {strength} password
                </MDTypography>
              </MDBox>
            )}
          </Grid>

          {/* Confirm Password */}
          <Grid item xs={12}>
            <RequiredLabel required>Confirm Password</RequiredLabel>
            <MDInput
              variant="outlined"
              fullWidth
              name="confirmPassword"
              type={show.confirm ? "text" : "password"}
              value={passwordForm.confirmPassword.value}
              onChange={(e) => handleChange(e.target.name, e.target.value)}
              onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
              error={!!passwordForm.confirmPassword.error}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => toggleVisibility("confirm")} edge="end">
                      <Icon>{show.confirm ? "visibility_off" : "visibility"}</Icon>
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {passwordForm.confirmPassword.error && (
              <MDTypography variant="caption" color="error">
                {passwordForm.confirmPassword.error}
              </MDTypography>
            )}
          </Grid>

          {/* Security note */}
          <Grid item xs={12}>
            <MDTypography
              variant="caption"
              color="text"
              display="flex"
              justifyContent ="center"
              alignItems="center"
            >
              <Icon fontSize="small" sx={{ mr: 0.5 }}>
                security
              </Icon>
              Your password is encrypted and securely stored
            </MDTypography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <MDButton
          variant="gradient"
          color="light"
          onClick={handleClose}
          disabled={loading}
        >
          Cancel
        </MDButton>

        <MDButton
          variant="contained"
          color="deepBlue"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Saving..." : "Save"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

ChangePasswordDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
};

export default ChangePasswordDialog;